import React from 'react';
import { Helmet } from 'react-helmet-async';
import { WeatherForm } from '../components/WeatherForm';
import { WeatherResult } from '../components/WeatherResult';
import { DatePicker } from '../components/DatePicker';
import { InstallPrompt } from '../components/InstallPrompt';
import { recordSearch } from '../utils/supabase';
import { MessageCircle, ExternalLink } from 'lucide-react';
import { fetchWeather, getClothingLayers, getTimeRelevantData } from '../utils/weather';
import type { WeatherData, TemperatureSensitivity } from '../types';

export const HomePage: React.FC = () => {
  const [weatherData, setWeatherData] = React.useState<WeatherData | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [searchCount, setSearchCount] = React.useState(0);
  const [hasSearched, setHasSearched] = React.useState(false);
  const [currentLocation, setCurrentLocation] = React.useState<string>('');
  const [selectedDate, setSelectedDate] = React.useState<Date>(new Date());
  const [sensitivity, setSensitivity] = React.useState<TemperatureSensitivity>('default');

  const handleSubmit = async (location: string, coordinates: { lat: number; lon: number }) => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchWeather(coordinates.lat, coordinates.lon);
      setWeatherData(data);
      setSearchCount(prev => prev + 1);
      setHasSearched(true);
      setCurrentLocation(location);
      
      // Record the search in Supabase
      const timeRelevantData = getTimeRelevantData(data, selectedDate);
      const recommendationTemp = data.current.apparent_temperature ?? data.current.temperature_2m;
      const recommendation = getClothingLayers(recommendationTemp, sensitivity);
      
      await recordSearch({
        city_name: location,
        latitude: coordinates.lat,
        longitude: coordinates.lon,
        temperature: data.current.temperature_2m,
        weather_code: timeRelevantData.weatherCode,
        recommendation_layers: recommendation.layers,
        sensitivity
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'שגיאה לא צפויה');
    } finally {
      setLoading(false);
    }
  };

  const handleContactClick = () => {
    window.open('https://wa.me/972557041852', '_blank');
  };

  const getDayIndex = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);
    
    const diffTime = selected.getTime() - today.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, Math.min(diffDays, 6));
  };

  const dayIndex = getDayIndex();
  const isToday = dayIndex === 0;
  
  const dailyTemp = weatherData?.daily.temperature_2m_max[dayIndex] ?? 20;
  const dailyTempMin = weatherData?.daily.temperature_2m_min[dayIndex];
  const dailyWindSpeed = weatherData?.daily.wind_speed_10m_max[dayIndex];
  const dailyWindDirection = weatherData?.daily.wind_direction_10m_dominant[dayIndex];
  
  const timeRelevantData = weatherData ? getTimeRelevantData(weatherData, selectedDate) : null;
  const weatherCode = timeRelevantData?.weatherCode;
  const precipProb = timeRelevantData?.precipProb;
  
  const currentTemp = isToday ? weatherData?.current.temperature_2m : undefined;
  const currentFeelsLike = isToday ? weatherData?.current.apparent_temperature : undefined;
  
  const avgTemp = !isToday && dailyTempMin !== undefined
    ? (dailyTemp + dailyTempMin) / 2
    : dailyTemp;
  
  const recommendationTemp = isToday ? (currentFeelsLike ?? currentTemp ?? dailyTemp) : avgTemp;
  const recommendation = getClothingLayers(recommendationTemp, sensitivity);

  const yesterdayTemp = isToday && weatherData ? weatherData.daily.temperature_2m_max[0] : undefined;

  return (
    <>
      <Helmet>
        <title>מה ללבוש היום? - המלצות לבוש לפי מזג האוויר</title>
        <meta name="description" content="קבלו המלצות לבוש מותאמות אישית לפי מזג האוויר במיקומכם" />
        <meta name="keywords" content="מזג אוויר, המלצות לבוש, תחזית מזג אוויר, מה ללבוש, ישראל" />
        <link rel="canonical" href="https://weather.automatzia.co.il" />
      </Helmet>

      <div className="min-h-screen bg-white flex flex-col">
        <InstallPrompt />
        
        <div className="max-w-lg mx-auto px-4 py-8 flex-grow">
          <div className="space-y-8">
            <div className="text-center">
              <h1 className="text-3xl font-bold text-slate-800 mb-4">
                כמה שכבות ללבוש היום?
              </h1>
              {!hasSearched && (
                <p className="text-slate-600">
                  ברוכים הבאים! בחרו מיקום או אפשרו גישה למיקום שלכם כדי לקבל המלצת לבוש
                </p>
              )}
            </div>

            <main className="space-y-6">
              <WeatherForm onSubmit={handleSubmit} isLoading={loading} />
              
              {error && (
                <div className="text-sm text-red-500 text-center">
                  {error}
                </div>
              )}

              {hasSearched && weatherData && !error && (
                <>
                  <div className="text-center text-slate-600 text-lg">
                    <span className="font-medium">{currentLocation}</span>
                  </div>

                  <DatePicker
                    selectedDate={selectedDate}
                    onDateSelect={setSelectedDate}
                  />

                  <WeatherResult
                    key={`${searchCount}-${dayIndex}-${sensitivity}`}
                    temperature={dailyTemp}
                    currentTemperature={currentTemp}
                    feelsLike={currentFeelsLike}
                    minTemperature={dailyTempMin}
                    recommendation={recommendation}
                    weatherCode={weatherCode}
                    precipitationProbability={precipProb}
                    windSpeed={dailyWindSpeed}
                    windDirection={dailyWindDirection}
                    onSensitivityChange={setSensitivity}
                    currentSensitivity={sensitivity}
                    yesterdayTemp={yesterdayTemp}
                  />
                </>
              )}
            </main>
          </div>
        </div>

        <footer className="mt-8 py-6 px-4 bg-slate-50">
          <div className="max-w-lg mx-auto">
            <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
              <div className="flex flex-col items-center gap-4">
                <div className="text-center">
                  <p className="text-slate-600 text-sm">
                    נבנה לכיף על ידי סתו ברנר
                  </p>
                  <p className="text-slate-600 text-sm">
                    אוטומציה ובינה מלאכותית לעסקים
                  </p>
                </div>

                <div className="w-48 h-12 relative">
                  <img 
                    src="https://res.cloudinary.com/dwrxrqqvu/image/upload/v1739191205/Logo_new_size_adraum.png"
                    alt="Automatzia Logo"
                    className="w-full h-full object-contain"
                  />
                </div>

                <div className="flex gap-3 w-full">
                  <button
                    onClick={handleContactClick}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors text-sm"
                  >
                    <MessageCircle size={18} />
                    <span>צרו קשר</span>
                  </button>

                  <a
                    href="https://automatzia-superpowers.webflow.io"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors text-sm"
                  >
                    <ExternalLink size={18} />
                    <span>בקרו באתר</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};